import React, { useEffect, useState, useRef } from 'react';
export const Fireworks = ({
  isActive = false,
  intensity = 1,
  color = 'default'
}) => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0
  });
  const particlesRef = useRef([]);
  const animationRef = useRef(null);
  // Set up canvas dimensions
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current) {
        const width = window.innerWidth;
        const height = window.innerHeight;
        canvasRef.current.width = width;
        canvasRef.current.height = height;
        setDimensions({
          width,
          height
        });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Firework particle class
  class Particle {
    constructor(x, y, particleSize, hue) {
      this.x = x;
      this.y = y;
      this.size = Math.random() * particleSize + 1;
      this.speedX = Math.random() * 6 - 3;
      this.speedY = Math.random() * 6 - 3;
      this.hue = hue;
      this.alpha = 1;
      this.friction = 0.95;
      this.gravity = 0.2;
      this.life = Math.random() * 40 + 40;
      this.isFading = false;
    }
    update() {
      this.speedY += this.gravity;
      this.x += this.speedX;
      this.y += this.speedY;
      this.size *= 0.97;
      this.life--;
      if (this.life < 20) {
        this.alpha -= 0.05;
        if (this.alpha < 0) this.alpha = 0;
      }
    }
    draw(ctx) {
      ctx.save();
      ctx.globalAlpha = this.alpha;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = `hsla(${this.hue}, 100%, 60%, ${this.alpha})`;
      ctx.fill();
      // Glow effect
      ctx.shadowBlur = 15;
      ctx.shadowColor = `hsla(${this.hue}, 100%, 60%, ${this.alpha})`;
      ctx.fill();
      ctx.restore();
    }
  }
  // Firework class
  class Firework {
    constructor(x, y, targetX, targetY, size, hue) {
      this.x = x;
      this.y = y;
      this.targetX = targetX;
      this.targetY = targetY;
      this.size = size;
      this.hue = hue;
      this.speed = 8;
      this.angle = Math.atan2(targetY - y, targetX - x);
      this.velocity = {
        x: Math.cos(this.angle) * this.speed,
        y: Math.sin(this.angle) * this.speed
      };
      this.trail = [];
      this.trailLength = 5;
      this.exploded = false;
      this.particles = [];
    }
    update() {
      this.trail.push({
        x: this.x,
        y: this.y
      });
      if (this.trail.length > this.trailLength) {
        this.trail.shift();
      }
      this.x += this.velocity.x;
      this.y += this.velocity.y;
      const distance = Math.hypot(this.targetX - this.x, this.targetY - this.y);
      if (distance < 5 && !this.exploded) {
        this.exploded = true;
        this.explode();
      }
      // Update particles if exploded
      if (this.exploded) {
        for (let i = this.particles.length - 1; i >= 0; i--) {
          this.particles[i].update();
          if (this.particles[i].alpha <= 0 || this.particles[i].size <= 0.2) {
            this.particles.splice(i, 1);
          }
        }
      }
    }
    explode() {
      const particleCount = Math.floor(Math.random() * 50) + 50;
      for (let i = 0; i < particleCount; i++) {
        this.particles.push(new Particle(this.x, this.y, this.size, this.hue));
      }
    }
    draw(ctx) {
      // Draw trail
      for (let i = 0; i < this.trail.length; i++) {
        const point = this.trail[i];
        const alpha = i / this.trail.length;
        ctx.beginPath();
        ctx.arc(point.x, point.y, this.size * alpha, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, 100%, 60%, ${alpha})`;
        ctx.fill();
      }
      // Draw firework if not exploded
      if (!this.exploded) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsl(${this.hue}, 100%, 60%)`;
        ctx.fill();
        // Glow effect
        ctx.shadowBlur = 15;
        ctx.shadowColor = `hsl(${this.hue}, 100%, 60%)`;
        ctx.fill();
      }
      // Draw particles if exploded
      if (this.exploded) {
        for (let i = 0; i < this.particles.length; i++) {
          this.particles[i].draw(ctx);
        }
      }
    }
    isFinished() {
      return this.exploded && this.particles.length === 0;
    }
  }
  // Animation loop
  useEffect(() => {
    if (!canvasRef.current || !isActive) return;
    const ctx = canvasRef.current.getContext('2d');
    const fireworks = [];
    // Color presets
    const colorSchemes = {
      default: () => Math.random() * 360,
      blue: () => Math.random() * 60 + 180,
      purple: () => Math.random() * 60 + 240,
      teal: () => Math.random() * 40 + 160,
      cyberpunk: () => [180, 300, 60][Math.floor(Math.random() * 3)] // Cyan, Purple, Yellow
    };
    const getHue = colorSchemes[color] || colorSchemes.default;
    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(0, 0, dimensions.width, dimensions.height);
      // Create new fireworks randomly
      if (Math.random() < 0.03 * intensity && fireworks.length < 8 * intensity) {
        const x = Math.random() * dimensions.width;
        const y = dimensions.height;
        const targetX = Math.random() * dimensions.width;
        const targetY = Math.random() * dimensions.height * 0.6;
        const size = Math.random() * 2 + 2;
        const hue = getHue();
        fireworks.push(new Firework(x, y, targetX, targetY, size, hue));
      }
      // Update and draw fireworks
      for (let i = fireworks.length - 1; i >= 0; i--) {
        fireworks[i].update();
        fireworks[i].draw(ctx);
        if (fireworks[i].isFinished()) {
          fireworks.splice(i, 1);
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, dimensions, intensity, color]);
  if (!isActive) return null;
  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-40" style={{
    opacity: 0.8
  }} data-id="element-745" />;
};